import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import "./BsDropdown.css";

/**
 * BsDropdown - 自定义 Dropdown 组件
 *
 * 解决 antd Dropdown 与 Tooltip 共存时的冲突问题：
 *   - Dropdown 打开时不渲染 Tooltip 包裹层，彻底避免 hover 冒泡
 *   - 使用 createPortal 渲染到 body，避免 overflow 裁剪
 *   - 带动画效果：fade + scale + slide
 *
 * Props:
 *   trigger       - 触发器内容 (ReactNode)
 *   overlay       - 面板内容 (ReactNode)
 *   open          - 可选受控开关状态
 *   placement     - 弹出位置: 'bottomLeft' | 'rightBottom' | 'bottomRight' | 'rightTop'
 *   disabled      - 是否禁用
 *   onOpenChange  - 开关回调 (open: boolean) => void
 *   overlayClassName - 面板额外 className
 */
const BsDropdown = ({
  children,
  overlay,
  open: controlledOpen,
  placement = "bottomLeft",
  disabled = false,
  onOpenChange,
  overlayClassName = "",
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = typeof controlledOpen === "boolean";
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [animating, setAnimating] = useState(false); // 动画状态
  const triggerRef = useRef(null);

  // 同步外部回调
  const handleOpenChange = useCallback(
    (nextOpen) => {
      if (!isControlled) setUncontrolledOpen(nextOpen);
      if (onOpenChange) onOpenChange(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  // 计算面板位置
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const isRight = placement.startsWith("right");
    const isTop = placement.endsWith("Top");

    let top, left;

    if (isRight) {
      // 右侧弹出
      top = isTop ? rect.bottom - rect.height : rect.top;
      left = rect.right + 8;
    } else {
      // 下方弹出
      top = isTop ? rect.top - 4 : rect.bottom + 4;
      left = placement === "bottomRight" ? rect.right : rect.left;
    }

    setPosition({ top, left });
  }, [placement]);

  // 打开：计算位置 + 启动入场动画
  const handleOpen = useCallback(() => {
    if (disabled) return;
    calculatePosition();
    setAnimating(true); // 先设为 animating（透明），下一帧显示
    // 用 requestAnimationFrame 确保位置已设置后再触发 transition
    requestAnimationFrame(() => {
      handleOpenChange(true);
    });
  }, [disabled, calculatePosition, handleOpenChange]);

  // 关闭：启动退场动画
  const handleClose = useCallback(() => {
    setAnimating(true); // 开始退场
    // 等 animationend 后真正关闭
    // 这里用 setTimeout 配合 CSS transition 时长
  }, []);

  // 关闭动画完成后真正隐藏
  useEffect(() => {
    if (!open && animating) {
      const timer = setTimeout(() => setAnimating(false), 180); // 匹配 CSS transition 时长
      return () => clearTimeout(timer);
    }
    if (open && !animating) {
      // 入场：下一帧添加 visible class
      const timer = setTimeout(() => setAnimating(false), 10);
      return () => clearTimeout(timer);
    }
  }, [open, animating]);

  // 点击外部关闭
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) {
        const panel = document.getElementById("bs-custom-drop");
        if (panel && !panel.contains(e.target)) {
          handleOpenChange(false);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, handleOpenChange]);

  // ESC 关闭
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") handleOpenChange(false);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, handleOpenChange]);

  // 判断是否应该渲染面板（包括动画期间）
  const shouldRenderPanel = open || animating;

  // 构建触发器内容
  const triggerContent = (
    <div
      ref={triggerRef}
      className={`bs-dropdown-trigger${disabled ? " bs-dropdown-trigger--disabled" : ""}`}
      onClick={open ? () => handleOpenChange(false) : handleOpen}
    >
      {children}
    </div>
  );

  // Portal 面板
  const panel =
    shouldRenderPanel &&
    ReactDOM.createPortal(
      <div
        id="bs-custom-drop"
        className={`bs-dropdown-panel ${overlayClassName} ${
          open ? "bs-dropdown-panel--visible" : ""
        } ${animating ? "bs-dropdown-panel--animating" : ""}`}
        style={{
          position: "fixed",
          top: position.top,
          left: position.left,
          zIndex: 1050,
        }}
        data-placement={placement}
      >
        {overlay}
      </div>,
      document.body,
    );

  return (
    <>
      {triggerContent}
      {panel}
    </>
  );
};

export default BsDropdown;
